import type { GraphEdge, NodeDefBase } from '../graph/nodeDefs.js';
import { getInvalidEdgeKeysFor } from '../editor/validationState.js';
import { edgeKey, type EdgeVisualStateController } from './edgeVisualState.js';
import { getEdgeVisual, getPendingConnectionVisual } from './edgeVisuals.js';
import type { PendingConnection } from '../editor/connectionMutations.js';
import type { AppState } from '../state/appState.js';

interface Point {
  x: number;
  y: number;
}

interface EdgeHandlers {
  onDisconnect: (fromNode: string, fromPort: number, toNode: string, toPort: number, ev: MouseEvent) => void;
  onPreview: (fromNode: string, fromPort: number, toNode: string, toPort: number, ev: MouseEvent) => void;
  onClearPreview: () => void;
  onShowHint: () => void;
}

interface EdgeElements {
  group: SVGGElement;
  glow: SVGPathElement;
  base: SVGPathElement;
  overlay: SVGPathElement;
  hit: SVGPathElement;
}

interface CachedConnectionRender {
  edges: GraphEdge[];
  appState: AppState;
  getDotWorldPos: (nodeId: string, portIdx: number, isOut: boolean) => Point;
  handlers: EdgeHandlers;
  nodeDefs: Record<string, NodeDefBase>;
}

export interface EdgeRendererController {
  renderConnections(
    edges: GraphEdge[],
    appState: AppState,
    getDotWorldPos: (nodeId: string, portIdx: number, isOut: boolean) => Point,
    handlers: EdgeHandlers,
    nodeDefs: Record<string, NodeDefBase>,
  ): void;
  renderPendingConnection(
    pendingConn: PendingConnection | null,
    appState: AppState,
    nodeDefs: Record<string, NodeDefBase>,
  ): void;
}

interface EdgeRendererDeps {
  visualState: EdgeVisualStateController;
}

function buildPath(x1: number, y1: number, x2: number, y2: number): string {
  const dx = Math.abs(x2 - x1) * 0.5;
  return `M${x1},${y1} C${x1 + dx},${y1} ${x2 - dx},${y2} ${x2},${y2}`;
}

function createEdgeElements(
  visualState: EdgeVisualStateController,
  svg: SVGSVGElement,
  edge: GraphEdge,
  handlers: EdgeHandlers,
  key: string,
  rerenderCachedConnections: () => void,
): EdgeElements {
  const group = document.createElementNS('http://www.w3.org/2000/svg', 'g');
  group.setAttribute('class', 'edge');
  group.dataset.key = key;

  const glow = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  glow.setAttribute('class', 'edge-glow');
  glow.setAttribute('fill', 'none');

  const base = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  base.setAttribute('class', 'edge-base');
  base.setAttribute('fill', 'none');

  const overlay = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  overlay.setAttribute('class', 'edge-overlay');
  overlay.setAttribute('fill', 'none');

  const hit = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  hit.setAttribute('class', 'edge-hit');
  hit.setAttribute('fill', 'none');
  hit.setAttribute('stroke', 'transparent');
  hit.setAttribute('stroke-width', '16');

  // Keep edge interactions from bubbling into viewport pan handling.
  hit.addEventListener('mousedown', ev => {
    // Let Ctrl+drag bubble to graphArea so edge-cut gesture can start.
    if (ev.ctrlKey || visualState.isCuttingEdges()) return;
    ev.stopPropagation();
    if (ev.button !== 0) return;
    ev.preventDefault();
    handlers.onDisconnect(edge.fromNode, edge.fromPort, edge.toNode, edge.toPort, ev);
  });

  hit.addEventListener('click', ev => {
    ev.stopPropagation();
    if (ev.ctrlKey || visualState.isCuttingEdges()) return;
    ev.preventDefault();
  });
  hit.addEventListener('mouseenter', ev => {
    if (ev.ctrlKey || visualState.isCuttingEdges()) {
      visualState.setHoveredEdgeKey(null);
      handlers.onClearPreview();
      rerenderCachedConnections();
      return;
    }
    visualState.setHoveredEdgeKey(key);
    handlers.onShowHint();
    rerenderCachedConnections();
  });
  hit.addEventListener('mousemove', ev => {
    if (ev.ctrlKey || visualState.isCuttingEdges()) {
      visualState.setHoveredEdgeKey(null);
      handlers.onClearPreview();
      rerenderCachedConnections();
      return;
    }
    handlers.onPreview(edge.fromNode, edge.fromPort, edge.toNode, edge.toPort, ev);
  });
  hit.addEventListener('mouseleave', () => {
    visualState.setHoveredEdgeKey(null);
    handlers.onClearPreview();
    rerenderCachedConnections();
  });

  group.append(glow, base, overlay, hit);
  svg.appendChild(group);

  return { group, glow, base, overlay, hit };
}

function applyPath(elements: EdgeElements, d: string): void {
  if (elements.glow.getAttribute('d') !== d) elements.glow.setAttribute('d', d);
  if (elements.base.getAttribute('d') !== d) elements.base.setAttribute('d', d);
  if (elements.overlay.getAttribute('d') !== d) elements.overlay.setAttribute('d', d);
  if (elements.hit.getAttribute('d') !== d) elements.hit.setAttribute('d', d);
}

function renderConnections(
  visualState: EdgeVisualStateController,
  pathCache: Map<string, EdgeElements>,
  rerenderCachedConnections: () => void,
  edges: GraphEdge[],
  appState: AppState,
  getDotWorldPos: (nodeId: string, portIdx: number, isOut: boolean) => Point,
  handlers: EdgeHandlers,
  nodeDefs: Record<string, NodeDefBase>
): void {
  const svg = document.getElementById('conn-svg') as unknown as SVGSVGElement;
  const activeKeys = new Set<string>();
  const invalidKeys = getInvalidEdgeKeysFor(appState);

  for (const edge of edges) {
    const key = edgeKey(edge.fromNode, edge.fromPort, edge.toNode, edge.toPort);
    activeKeys.add(key);
    const from = getDotWorldPos(edge.fromNode, edge.fromPort, true);
    const to = getDotWorldPos(edge.toNode, edge.toPort, false);
    let elements = pathCache.get(key);
    if (!elements) {
      elements = createEdgeElements(visualState, svg, edge, handlers, key, rerenderCachedConnections);
      pathCache.set(key, elements);
    }

    const d = buildPath(from.x, from.y, to.x, to.y);
    applyPath(elements, d);

    const visual = getEdgeVisual(visualState, edge, appState.nodes, nodeDefs);
    const stateName = invalidKeys.has(key) && visual.state === 'default' ? 'invalid' : visual.state;
    elements.group.dataset.type = visual.type ?? 'unknown';
    elements.group.dataset.state = stateName;
    elements.group.classList.toggle('is-dimmed', visual.dimmed);
    elements.group.style.setProperty('--edge-color', visual.color);
    elements.group.style.setProperty('--edge-opacity', visual.dimmed ? '0.62' : '0.92');
  }

  for (const [key, elements] of pathCache.entries()) {
    if (activeKeys.has(key)) continue;
    elements.group.remove();
    pathCache.delete(key);
  }
}

function renderPendingConnection(
  pendingConn: PendingConnection | null,
  appState: AppState,
  nodeDefs: Record<string, NodeDefBase>,
): void {
  const svg = document.getElementById('conn-svg') as unknown as SVGSVGElement;
  svg.querySelectorAll('.pending-conn').forEach(el => el.remove());
  document.body.classList.toggle('dragging-conn', !!pendingConn);
  if (!pendingConn) return;

  const group = document.createElementNS('http://www.w3.org/2000/svg', 'g');
  group.setAttribute('class', 'edge pending-conn');
  const base = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  const glow = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  const overlay = document.createElementNS('http://www.w3.org/2000/svg', 'path');

  const outPos = pendingConn.isFromOut ? pendingConn.from : pendingConn.current;
  const inPos = pendingConn.isFromOut ? pendingConn.current : pendingConn.from;
  const d = buildPath(outPos.x, outPos.y, inPos.x, inPos.y);
  const visual = getPendingConnectionVisual(pendingConn, appState.nodes, nodeDefs);

  group.dataset.state = visual.state;
  group.style.setProperty('--edge-color', visual.color);
  glow.setAttribute('class', 'edge-glow');
  glow.setAttribute('fill', 'none');
  base.setAttribute('class', 'edge-base');
  base.setAttribute('fill', 'none');
  overlay.setAttribute('class', 'edge-overlay');
  overlay.setAttribute('fill', 'none');
  glow.setAttribute('d', d);
  base.setAttribute('d', d);
  overlay.setAttribute('d', d);
  group.append(glow, base, overlay);
  svg.appendChild(group);
}

export function createEdgeRendererController(deps: EdgeRendererDeps): EdgeRendererController {
  const pathCache = new Map<string, EdgeElements>();
  let lastRender: CachedConnectionRender | null = null;

  function rerenderCachedConnections(): void {
    if (!lastRender) return;
    renderConnections(
      deps.visualState,
      pathCache,
      rerenderCachedConnections,
      lastRender.edges,
      lastRender.appState,
      lastRender.getDotWorldPos,
      lastRender.handlers,
      lastRender.nodeDefs,
    );
  }

  return {
    renderConnections(edges, appState, getDotWorldPos, handlers, nodeDefs): void {
      lastRender = { edges, appState, getDotWorldPos, handlers, nodeDefs };
      renderConnections(deps.visualState, pathCache, rerenderCachedConnections, edges, appState, getDotWorldPos, handlers, nodeDefs);
    },
    renderPendingConnection,
  };
}
